import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/api/client";
import type { components } from "@dolfin/api-admin";
import { Badge } from "@/components/ui/badge";
import { Spinner } from "@/components/ui/spinner";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { PaginationBar } from "@/components/PaginationBar";
import { useCursorPagination } from "@/hooks/useCursorPagination";
import { formatCurrency, formatDate, formatDateTime } from "@/lib/format";

type PurchaseOrder = components["schemas"]["PurchaseOrderResponse"];
type PurchaseOrderStatus = components["schemas"]["PurchaseOrderStatus"];

interface PurchaseOrdersTableProps {
  organisationId?: string;
}

function statusVariant(
  status: PurchaseOrderStatus | undefined,
): "default" | "secondary" | "outline" | "destructive" {
  switch (status) {
    case "FullyMatched":
      return "default";
    case "PartiallyMatched":
      return "secondary";
    case "Cancelled":
      return "destructive";
    default:
      return "outline";
  }
}

function grossTotal(po: PurchaseOrder): number {
  return (po.lines ?? []).reduce((sum, l) => {
    const net = typeof l.subTotal === "string" ? parseFloat(l.subTotal) : (l.subTotal ?? 0);
    const tax = typeof l.taxAmount === "string" ? parseFloat(l.taxAmount) : (l.taxAmount ?? 0);
    return sum + net + tax;
  }, 0);
}

export function PurchaseOrdersTable({ organisationId }: PurchaseOrdersTableProps) {
  const pagination = useCursorPagination({ initialLimit: 20 });

  const [orders, setOrders] = useState<PurchaseOrder[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;

    async function fetchOrders() {
      setLoading(true);
      setError(null);
      const { data, error: apiError } = await api.GET("/admin/purchase-orders", {
        params: {
          query: {
            OrganisationId: organisationId,
            Cursor: pagination.cursor,
            Limit: pagination.limit,
          },
        },
      });
      if (cancelled) return;
      if (apiError) {
        setError("Failed to load purchase orders.");
      } else {
        setOrders(data?.data ?? []);
        pagination.setPage({
          nextCursor: data?.meta?.cursors?.next,
          previousCursor: data?.meta?.cursors?.previous,
        });
      }
      setLoading(false);
    }

    fetchOrders();
    return () => {
      cancelled = true;
    };
  }, [organisationId, pagination.cursor, pagination.limit]);

  if (error) {
    return <p className="text-destructive">{error}</p>;
  }

  return (
    <div>
      <div className="mb-4 flex items-center gap-3">{loading && <Spinner className="size-5" />}</div>

      <div className="rounded-md border">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>PO #</TableHead>
              <TableHead>Status</TableHead>
              <TableHead>Supplier</TableHead>
              <TableHead>Order date</TableHead>
              <TableHead>Expected</TableHead>
              <TableHead>Lines</TableHead>
              <TableHead>Total</TableHead>
              <TableHead>Last modified</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {orders.length === 0 && !loading ? (
              <TableRow>
                <TableCell colSpan={8} className="text-muted-foreground h-24 text-center">
                  No purchase orders found.
                </TableCell>
              </TableRow>
            ) : (
              orders.map((po) => (
                <TableRow key={po.id}>
                  <TableCell className="font-mono font-medium">
                    {po.id ? (
                      <Link to={`/purchase-orders/${po.id}`} className="hover:underline">
                        {po.poNumber || po.id.slice(0, 8) + "..."}
                      </Link>
                    ) : (
                      (po.poNumber ?? <span className="text-muted-foreground">—</span>)
                    )}
                  </TableCell>
                  <TableCell>
                    <Badge variant={statusVariant(po.status)}>{po.status}</Badge>
                  </TableCell>
                  <TableCell>
                    {po.supplierId ? (
                      <Link to={`/suppliers/${po.supplierId}`} className="hover:underline">
                        <Badge variant="secondary" className="font-mono text-xs hover:bg-primary/20">
                          {po.supplierId.slice(0, 8)}...
                        </Badge>
                      </Link>
                    ) : (
                      <span className="text-muted-foreground">—</span>
                    )}
                  </TableCell>
                  <TableCell>{formatDate(po.orderDate) || <span className="text-muted-foreground">—</span>}</TableCell>
                  <TableCell>
                    {formatDate(po.expectedDeliveryDate) || <span className="text-muted-foreground">—</span>}
                  </TableCell>
                  <TableCell>{po.lines?.length ?? 0}</TableCell>
                  <TableCell>{formatCurrency(grossTotal(po), po.currency ?? "GBP")}</TableCell>
                  <TableCell className="text-muted-foreground text-sm">
                    {formatDateTime(po.updatedAt)}
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </div>

      <PaginationBar
        hasPrevious={pagination.hasPrevious}
        hasNext={pagination.hasMore}
        isLoading={loading}
        limit={pagination.limit}
        onPrevious={pagination.goPrevious}
        onNext={pagination.goNext}
        onLimitChange={pagination.setLimit}
      />
    </div>
  );
}
